/**
 * MyToolsPanel
 * Shows tools unlocked on the user's match profile (purchased bundle or free shares).
 * If user has any unlocked tools → renders quick-access cards.
 * If user has zero tools → renders an upsell to the $97 bundle.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Calculator, FileSearch, MessageSquareText, ChevronRight, Sparkles, Lock, Scale, Gift } from 'lucide-react';

const TOOL_DEFS = {
  cost_calculator: {
    label: 'Cost Calculator',
    desc: 'See true 5-year ownership cost',
    icon: Calculator,
    accent: 'bg-emerald-500/15 text-emerald-500',
    border: 'hover:border-emerald-500',
    href: (sessionId) => `/vip/${sessionId}/cost-calculator`,
  },
  deal_analyzer: {
    label: 'Deal Analyzer',
    desc: 'Is this offer fair? Get the verdict',
    icon: FileSearch,
    accent: 'bg-sky-500/15 text-sky-500',
    border: 'hover:border-sky-500',
    href: (sessionId) => `/vip/${sessionId}/deal-analyzer`,
  },
  negotiation_coach: {
    label: 'Negotiation Coach',
    desc: 'Word-for-word scripts that close deals',
    icon: MessageSquareText,
    accent: 'bg-rose-500/15 text-rose-500',
    border: 'hover:border-rose-500',
    href: (sessionId) => `/vip/${sessionId}/negotiation-coach`,
  },
};

const MyToolsPanel = ({ unlockedTools = [], sessionId, isDark }) => {
  const owned = (unlockedTools || []).filter(t => TOOL_DEFS[t]);

  // Free tools strip — always visible to every customer
  const FreeToolsStrip = () => (
    <div
      className={`rounded-2xl p-5 ${isDark ? 'bg-gradient-to-br from-emerald-900/20 to-gray-900 border border-emerald-700/40' : 'bg-gradient-to-br from-emerald-50 to-white border border-emerald-200'}`}
      data-testid="free-tools-strip"
    >
      <div className="flex items-center gap-2 mb-3">
        <Gift className="w-4 h-4 text-emerald-500" />
        <h4 className={`text-sm font-bold uppercase tracking-wider ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
          Free tools — included with every match dashboard
        </h4>
      </div>
      <Link
        to={sessionId ? `/tool/rv-compare/${sessionId}` : '/tool/rv-compare'}
        className={`group flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
          isDark ? 'border-emerald-700/40 bg-gray-800/40 hover:border-emerald-500' : 'border-emerald-200 bg-white hover:border-emerald-400 hover:shadow-md'
        }`}
        data-testid="tool-card-rv-compare"
      >
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}>
          <Scale className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>RV vs RV — AI Comparison</p>
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500 text-white">FREE</span>
          </div>
          <p className={`text-xs sm:text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Drop two RVs in side-by-side — AI scores them across 7 categories with a clear winner.
          </p>
        </div>
        <ChevronRight className={`shrink-0 transition-transform group-hover:translate-x-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} size={18} />
      </Link>
    </div>
  );

  // Empty state — upsell + free tools
  if (owned.length === 0) {
    return (
      <div className="space-y-4">
        <FreeToolsStrip />
        <div
          className={`rounded-2xl border-2 border-dashed p-5 sm:p-6 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-300 bg-white'}`}
          data-testid="my-tools-empty"
        >
          <div className="flex items-start gap-4">
            <div className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-[#F2A900]/20 text-[#F2A900]' : 'bg-[#F2A900]/15 text-[#F2A900]'}`}>
              <Lock className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className={`text-base sm:text-lg font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Unlock your premium RV Buying Tools
              </h3>
              <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Cost Calculator, Deal Analyzer & Negotiation Coach — see the true cost, verify the price, and lock in a fair deal.
              </p>
              <Link
                to="/tools-bundle"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F2A900] hover:brightness-110 text-black text-sm font-bold transition-all"
                data-testid="my-tools-bundle-cta"
              >
                Get all 3 — $97 <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Owned state — free tools strip + quick access cards
  return (
    <div className="space-y-4">
      <FreeToolsStrip />
      <div
        className={`rounded-2xl ${isDark ? 'bg-gray-900/60 border border-gray-800' : 'bg-white border border-gray-200'} p-5 sm:p-6 shadow-sm`}
        data-testid="my-tools-panel"
      >
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-[#F2A900]" />
        <h3 className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Your RV Buying Tools
        </h3>
        <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
          {owned.length}/{Object.keys(TOOL_DEFS).length} unlocked
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {Object.entries(TOOL_DEFS).map(([key, def]) => {
          const isOwned = owned.includes(key);
          const Icon = def.icon;
          if (!isOwned) {
            return (
              <Link
                key={key}
                to="/tools-bundle"
                className={`group relative p-4 rounded-xl border-2 border-dashed transition-colors ${isDark ? 'border-gray-700 bg-gray-800/30 hover:border-gray-500' : 'border-gray-300 bg-gray-50 hover:border-gray-400'}`}
                data-testid={`tool-locked-${key}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-gray-700/50 text-gray-500' : 'bg-gray-200 text-gray-400'}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <Lock size={14} className="ml-auto text-gray-400" />
                </div>
                <p className={`text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{def.label}</p>
                <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Bundle unlocks this</p>
              </Link>
            );
          }
          return (
            <Link
              key={key}
              to={sessionId ? def.href(sessionId) : '/my-quiz'}
              className={`group p-4 rounded-xl border-2 transition-all ${isDark ? 'border-gray-800 bg-gray-800/40 hover:bg-gray-800' : 'border-gray-200 bg-white hover:shadow-md'} ${def.border}`}
              data-testid={`tool-card-${key}`}
            >
              <div className={`w-10 h-10 rounded-lg ${def.accent} flex items-center justify-center mb-3`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{def.label}</p>
              <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{def.desc}</p>
              <span className={`inline-flex items-center gap-1 mt-2 text-xs font-semibold ${isDark ? 'text-[#F2A900]' : 'text-[#004E86]'}`}>
                Open tool <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          );
        })}
      </div>
      </div>
    </div>
  );
};

export default MyToolsPanel;
